<script setup lang="ts">
// ================================
// IMPORTS
// ================================
import { computed } from "vue"
import { useI18n } from "vue-i18n"
import { storeToRefs } from "pinia"
import { useSubscriptionFormStore } from "@modules/abonnement/stores/subscription-form.store"
import { useAuthStore } from "@core/stores/auth.store"
import {
    TEXTE_AU_DELA_SOUSCRIPTION,
    TEXTE_BASE_SOUSCRIPTION,
    textePlafondChoisi,
    texteFactureesAujourdhui,
} from "@modules/abonnement/utils/souscription-sources"

// ================================
// INTERNATIONALIZATION
// ================================
const { t } = useI18n({
    messages: {
        fr: {
            title: "Nombre de sources",
            subtitle1: "Combien de sources souhaitez-vous inclure ?",
            subtitle2: TEXTE_BASE_SOUSCRIPTION,
            sources: "sources",
            for: "pour",
            month: "mois",
            year: "an",
            default: "par défaut",
            included: "inclus",
            hint_sources:
                "30 sources suffisent pour 25 bâtiments en moyenne (évolutif à tout moment)",
            placeholder_sources: "Choisir le nombre de sources",
            more_sources: TEXTE_AU_DELA_SOUSCRIPTION,
            previous: "Précédent",
            next: "Continuer",
        },
    },
})

// ================================
// COMPOSABLES
// ================================
const subscriptionFormStore = useSubscriptionFormStore()
const { getSourcesPlans, offerSourceSelected, commitment } = storeToRefs(
    subscriptionFormStore
)
const { setOfferSourceSelected } = subscriptionFormStore
const authStore = useAuthStore()

// ================================
// PROPS & EMITS
// ================================
const emit = defineEmits<{
    (e: "back"): void
    (e: "next"): void
}>()

// ================================
// COMPUTED
// ================================
/** Prix du plan de base (premier plan = ex. 30 sources). Utilisé pour calculer le supplément affiché. */
const getBasePlanPrice = computed(() => {
    const plans = getSourcesPlans.value
    return plans.length ? plans[0].price : 0
})

/**
 * Supplément mensuel par rapport au plan de base (même logique que la ligne "+ N sources" du summary).
 * 0 pour le plan par défaut.
 */
function getExtraPriceForPlan(plan: { price: number }): number {
    return Math.max(0, plan.price - getBasePlanPrice.value)
}

/** Options pour le menu déroulant (id + libellé affiché). Affiche le supplément ajouté au total, pas le prix total du plan. */
const getSourcePlanOptions = computed(() => {
    const plans = getSourcesPlans.value
    const withYear = commitment.value
    const basePrice = getBasePlanPrice.value
    return plans.map((plan, index) => {
        const extra = plan.price - basePrice
        return {
            id: plan.id,
            label:
                index === 0
                    ? `${plan.sources} • ${t("default")}`
                    : extra <= 0
                      ? `${plan.sources} ${t("sources")} • ${t("included")}`
                      : withYear
                        ? `${plan.sources} ${t("sources")} • +${(extra * 12).toFixed(0)} € / ${t("year")} (+${extra} € / ${t("month")})`
                        : `${plan.sources} ${t("sources")} • +${extra} € / ${t("month")}`,
        }
    })
})

/** Plan de sources actuellement sélectionné. */
const getCurrentPlan = computed(() => {
    const plans = getSourcesPlans.value
    const selectedId = offerSourceSelected.value
    const found = plans.find((p) => p.id === selectedId)
    if (found) return found
    return plans.length ? plans[0] : null
})

/** Supplément du plan actuel (ce qui s'ajoute au total, comme dans le summary). */
const getCurrentPlanExtra = computed(() => {
    const plan = getCurrentPlan.value
    return plan ? getExtraPriceForPlan(plan) : 0
})

/** Plafond choisi : « N sources par période » (DEV-621, M11) */
const getPlafondChoisi = computed(() =>
    getCurrentPlan.value
        ? textePlafondChoisi(getCurrentPlan.value.sources)
        : null
)

/**
 * Sources facturées aujourd'hui, si le compte connecté en a (plafond servi) :
 * aide au choix, sans règle de facturation. `null` : phrase omise.
 */
const getFactureesAujourdhui = computed(() =>
    texteFactureesAujourdhui(authStore.companyPlafond)
)

// ================================
// INTERACTIONS
// ================================
/** Met à jour le plan de sources sélectionné. */
function onChangeSource(planId: string | null): void {
    if (planId) setOfferSourceSelected(planId)
}
</script>

<template>
    <BaseCard
        class="subscription-form-sources"
        :pt="{
            header: 'mx-auto p-6',
            content: 'flex flex-col items-center gap-10 px-6 md:px-10',
        }"
    >
        <template #header>
            <h1
                class="text-xl font-semibold text-primary-900 text-center flex items-center gap-2"
            >
                {{ t("title") }}
            </h1>
        </template>
        <template #content>
            <div class="flex flex-col items-center gap-6 w-full">
                <h2 class="text-base font-medium text-primary-800">
                    {{ t("subtitle1") }}
                </h2>
                <h3 class="text-sm text-primary-700">
                    {{ t("subtitle2") }}
                </h3>

                <div
                    v-if="getCurrentPlan"
                    class="flex flex-row items-center justify-center gap-2 flex-wrap"
                >
                    <span
                        class="subscription-form-sources__plafond text-base font-medium"
                        >{{ getPlafondChoisi }}</span
                    >
                    <template v-if="getCurrentPlanExtra > 0">
                        <span class="text-sm text-primary-600">{{
                            t("for")
                        }}</span>
                        <span class="text-xl font-semibold">
                            +{{
                                commitment
                                    ? (getCurrentPlanExtra * 12).toFixed(0)
                                    : getCurrentPlanExtra
                            }}
                            €
                        </span>
                        <span class="text-sm text-primary-400">
                            / {{ commitment ? t("year") : t("month") }}
                            <span v-if="commitment" class="text-primary-500">
                                (+{{ getCurrentPlanExtra }} € /
                                {{ t("month") }})
                            </span>
                        </span>
                    </template>
                    <span v-else class="text-sm text-primary-600">
                        • {{ t("included") }}
                    </span>
                </div>

                <div class="w-full max-w-md mx-auto">
                    <BaseSelect
                        id="subscription-form-sources"
                        :model-value="offerSourceSelected"
                        :options="getSourcePlanOptions"
                        option-value="id"
                        option-label="label"
                        :placeholder="t('placeholder_sources')"
                        class="w-full"
                        @update:model-value="onChangeSource"
                    />
                </div>

                <p
                    v-if="getFactureesAujourdhui"
                    class="subscription-form-sources__facturees text-sm text-primary-800 text-center max-w-md mx-auto"
                >
                    {{ getFactureesAujourdhui }}
                </p>

                <p
                    class="text-xs text-primary-600 text-center max-w-md mx-auto"
                >
                    {{ t("hint_sources") }}
                </p>

                <p class="text-sm text-primary-900 text-center">
                    {{ t("more_sources") }}
                    <a
                        href="mailto:contact@homeys.io"
                        class="text-primary-700 underline"
                        >contact@homeys.io</a
                    >
                </p>
            </div>

            <div class="w-full flex flex-row gap-4 mt-4 justify-between">
                <BaseButton
                    :label="t('previous')"
                    severity="secondary"
                    outlined
                    @click="emit('back')"
                />
                <BaseButton
                    :label="t('next')"
                    :disabled="!offerSourceSelected"
                    @click="emit('next')"
                />
            </div>
        </template>
    </BaseCard>
</template>
